import React, { useState, useEffect, useCallback, CSSProperties } from 'react';
import { Container, Row, Button, Badge, Spinner, Alert, Accordion } from 'react-bootstrap';
import { Box, Tabs, Tab } from '@mui/material';
import { FilterAutocomplete } from '../Trading/Filters/FilterField';
import {
  ExchangeType,
  PlatformType,
  FixActionStatus,
  FixSessionData,
  PLATFORMS_BY_EXCHANGE,
  EXCHANGES,
  SESSION_ROW_META,
  TRADE_CAPTURE_KEY,
  ALL_ROW_KEYS,
} from './Exchangeconnectivitymanagement.types';
import { fetchFixConnectionStatus, fetchFixSessionStatus, toggleFixConnection } from './Exchangeconnectivitymanagement.actions';

const scopedCss = `
.ecm-widget { animation: ecm-fade 180ms ease; }
@keyframes ecm-fade { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }

.ecm-btn { transition: filter 120ms ease, transform 80ms ease; }
.ecm-btn:hover:not(:disabled) { filter: brightness(1.07); }
.ecm-btn:active:not(:disabled) { transform: scale(0.97); }
.ecm-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.ecm-icon-btn { transition: background 120ms ease, border-color 120ms ease, color 120ms ease; }
.ecm-icon-btn:hover { background: #eaf5fd !important; border-color: #2f9bd6 !important; color: #1478b5 !important; }

.ecm-tl-row { transition: background 120ms ease; border-radius: 8px; }
.ecm-tl-row:hover { background: #f7f9fb; }

.ecm-copy:hover { background: #eef2f6 !important; }

.ecm-expand-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 200ms ease; }
.ecm-expand-wrap.open { grid-template-rows: 1fr; }
.ecm-expand-inner { overflow: hidden; }

.ecm-result-card { animation: ecm-fade 200ms ease; }
`;

const MARKER_COL_WIDTH = 16;
const FILTER_WIDTH = { width: 220 };

const styles: Record<string, CSSProperties> = {
  wrapper: { width: '100%' },

  controlCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  controlBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
  },
  statusPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12.5,
    color: '#1f2937',
    fontWeight: 600,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
    boxShadow: '0 0 0 3px rgba(0,0,0,0.04)',
  },
  toggleBtn: {
    border: 'none',
    fontWeight: 700,
    fontSize: 11.5,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    padding: '5px 14px',
    borderRadius: 6,
    lineHeight: 1.2,
    minWidth: 118,
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(16,24,40,0.18)',
    marginLeft: 'auto',
  },

  resultCard: {
    borderTop: '1px solid #eef0f2',
    borderLeft: '4px solid #22c55e',
    padding: '6px 10px',
  },
  resultCardFail: {
    borderTop: '1px solid #eef0f2',
    borderLeft: '4px solid #ef4444',
    padding: '6px 10px',
  },
  resultHeaderRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 },
  resultStatusText: { fontSize: 12.5, fontWeight: 700, letterSpacing: '0.02em' },
  resultLine: { fontSize: 12, color: '#374151', marginTop: 1, lineHeight: 1.3 },
  resultLineLabel: { fontWeight: 600, color: '#111827' },

  activityCard: {
    background: '#ffffff',
    border: '1px solid #eef0f2',
    borderRadius: 14,
    boxShadow: '0 2px 8px rgba(16,24,40,0.06)',
    padding: '4px 8px 2px',
    marginTop: 6,
  },
  activityHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginTop: 0,
    marginBottom: 0,
    paddingBottom: 0,
    gap: 4,
    lineHeight: 1,
  },
  activityTitleRow: { display: 'flex', alignItems: 'center', gap: 6 },
  activityAccentBar: { width: 4, height: 20, borderRadius: 3, background: '#2f9bd6', flexShrink: 0 },
  activityTitle: { fontSize: 15.5, fontWeight: 800, color: '#111827', letterSpacing: '-0.01em' },
  activitySubtitle: { fontSize: 11, color: '#9ca3af', marginTop: 4 },

  headerRight: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  iconBtn: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #dde2e7',
    borderRadius: 999,
    background: '#fff',
    color: '#6b7280',
    fontSize: 11,
    cursor: 'pointer',
  },
  iconBtnActive: { background: '#eaf5fd', borderColor: '#2f9bd6', color: '#1478b5' },

  maxViewList: { display: 'flex', flexDirection: 'column', gap: 0, marginTop: 0, marginBottom: 0 },
  maxViewItem: { padding: '0 2px', margin: 0, borderBottom: '1px solid #eef0f2' },
  maxViewItemLast: { padding: '0 2px', margin: 0 },
  maxViewHeadRow: { display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 0, marginBottom: 0, lineHeight: 1.1 },
  maxViewLabel: { fontSize: 12.5, fontWeight: 700, color: '#111827' },
  maxViewTime: {
    fontSize: 11,
    color: '#6b7280',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  fixTagGrid: {
    background: '#f4f6f8',
    border: '1px solid #e5e9ed',
    borderRadius: 8,
    padding: '3px 6px',
    margin: 0,
    fontSize: 11.5,
    color: '#1f2937',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.15,
    wordBreak: 'break-all',
  },
  fixTagValue: { color: '#1f2937', fontWeight: 600, wordBreak: 'break-all' },

  timeline: { position: 'relative', paddingTop: 0 },
  timelineLine: {
    position: 'absolute',
    top: 4,
    bottom: 4,
    left: MARKER_COL_WIDTH / 2 - 1,
    width: 2,
    background:
      'repeating-linear-gradient(to bottom, #e1e5ea 0, #e1e5ea 3px, transparent 3px, transparent 6px)',
    zIndex: 0,
  },
  tlRowOuter: { position: 'relative', zIndex: 1 },
  tlRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '2px 4px',
    cursor: 'pointer',
    gap: 6,
  },
  markerCol: { width: MARKER_COL_WIDTH, display: 'flex', justifyContent: 'center', flexShrink: 0, paddingTop: 1 },
  tlDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, boxShadow: '0 0 0 3px #fff' },
  tlOuterCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    minWidth: 0,
  },
  tlContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
    minWidth: 0,
  },
  tlLabel: { fontSize: 12.5, fontWeight: 600, color: '#1f2937', lineHeight: 1.15, whiteSpace: 'nowrap' },
  tlSep: { fontSize: 11, color: '#c3c9d1' },
  tlTimePill: {
    fontSize: 11,
    fontWeight: 500,
    color: '#6b7280',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
  },
  chevron: {
    fontSize: 9,
    color: '#9ca3af',
    transition: 'transform 180ms ease',
    flexShrink: 0,
    lineHeight: 1,
  },
  chevronOpen: { transform: 'rotate(180deg)', color: '#2f9bd6' },

  expandPanelOuter: { margin: '0 4px 3px 22px' },
  expandPanel: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: 6,
    rowGap: 2,
    padding: '5px 8px',
    background: '#f4f6f8',
    border: '1px solid #e5e9ed',
    borderRadius: 8,
    fontSize: 11.5,
    color: '#1f2937',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.35,
    wordBreak: 'break-all',
    maxHeight: 190,
    overflowY: 'auto',
  },
  expandPanelText: { flex: '1 1 auto', minWidth: 0 },
  fieldPrefix: { color: '#9ca3af', fontWeight: 700, marginRight: 3 },
  copyBtn: {
    display: 'inline-flex',
    flexShrink: 0,
    border: '1px solid #d7dbe0',
    background: '#fff',
    color: '#4b5563',
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    borderRadius: 5,
    padding: '2px 7px',
  },
  centerRow: { display: 'flex', justifyContent: 'center', padding: '8px 0' },
};

const formatTime = (t?: string) => (t ? t : '—');

const ExchangeConnectivityManagement: React.FC = () => {
  const [selectedExchange, setSelectedExchange] = useState<ExchangeType>('ICE');
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformType>('WebICE');

  const [connected, setConnected] = useState<boolean | null>(null);
  const [actionResult, setActionResult] = useState<FixActionStatus | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [sessionData, setSessionData] = useState<FixSessionData | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [maxView, setMaxView] = useState(false);
  const [disconnectCooldown, setDisconnectCooldown] = useState(false);

  const allExpanded = expandedKeys.size === ALL_ROW_KEYS.length;

  const fetchSessionStatus = useCallback(async () => {
    setSessionLoading(true);
    setSessionError(null);
    try {
      const data = await fetchFixSessionStatus(selectedPlatform);
      setSessionData(data);
      setLastFetched(new Date().toLocaleTimeString());
    } catch (err) {
      setSessionError('Failed to load session status.');
      setSessionData(null);
    } finally {
      setSessionLoading(false);
    }
  }, [selectedPlatform]);

  useEffect(() => {
    setActionResult(null);
    setActionError(null);
    setExpandedKeys(new Set());
    fetchSessionStatus();
  }, [selectedPlatform, fetchSessionStatus]);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const { connected } = await fetchFixConnectionStatus(selectedPlatform);
        setConnected(connected);
      } catch {
        setConnected(null);
      }
    };
    loadStatus();
  }, [selectedPlatform]);

  const handleExchangeSelect = (exchange: ExchangeType) => {
    setSelectedExchange(exchange);
    setSelectedPlatform(PLATFORMS_BY_EXCHANGE[exchange][0]);
  };

  const handleToggleConnection = async () => {
    const isDisconnectAction = connected === true;
    setActionLoading(true);
    setActionError(null);
    try {
      const data = await toggleFixConnection(selectedPlatform, !!connected);
      setActionResult(data.data.status);
      setConnected(data.data.status.connected);
    } catch (err) {
      setActionError('Action failed. Please try again.');
    } finally {
      setActionLoading(false);
      fetchSessionStatus();
      if (isDisconnectAction) {
        setDisconnectCooldown(true);
        setTimeout(() => setDisconnectCooldown(false), 15000);
      }
    }
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleExpandAll = () => {
    setExpandedKeys(allExpanded ? new Set() : new Set(ALL_ROW_KEYS));
  };

  const handleCopy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1200);
    } catch {
    }
  };

  return (
    <div className="page-body">
      <style>{scopedCss}</style>
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                   Connectivity Console
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="ecm-widget" style={styles.wrapper}>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 0 }}>
                        <Tabs
                          value={EXCHANGES.indexOf(selectedExchange)}
                          onChange={(_e, newValue: number) => handleExchangeSelect(EXCHANGES[newValue])}
                          aria-label="exchange selection tabs"
                          textColor="primary"
                          indicatorColor="primary"
                        >
                          {EXCHANGES.map((exchange) => (
                            <Tab
                              key={exchange}
                              label={exchange}
                              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', minWidth: 90 }}
                            />
                          ))}
                        </Tabs>
                      </Box>

                      <Box sx={{ ...FILTER_WIDTH, mb: 2 }}>
                        <FilterAutocomplete
                          label="Platform"
                          options={PLATFORMS_BY_EXCHANGE[selectedExchange].map((platform: PlatformType) => ({
                            value: platform,
                            label: platform,
                          }))}
                          value={selectedPlatform}
                          onChange={(value: string) => setSelectedPlatform(value as PlatformType)}
                          placeholder="Select Platform"
                        />
                      </Box>

                      <div style={styles.section}>
                        <div style={styles.controlCard}>
                          <div style={styles.controlBar}>
                            <span style={styles.statusPill}>
                              <span
                                style={{
                                  ...styles.dot,
                                  background:
                                    connected === null
                                      ? "#9ca3af"
                                      : connected
                                        ? "#22c55e"
                                        : "#ef4444",
                                }}
                              />
                              Connection Status:{" "}
                              <strong>
                                {connected === null
                                  ? "Unknown"
                                  : connected
                                    ? "Connected"
                                    : "Disconnected"}
                              </strong>
                            </span>

                            <Button
                              className="ecm-btn"
                              style={{
                                ...styles.toggleBtn,
                                background: connected ? "#ef4444" : "#22c55e",
                              }}
                              onClick={handleToggleConnection}
                              disabled={actionLoading || disconnectCooldown}
                            >
                              {actionLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : connected ? (
                                "⏻ Disconnect"
                              ) : (
                                "⏻ Connect"
                              )}
                            </Button>
                            <Button
                              className="ecm-btn"
                              style={{
                                ...styles.toggleBtn,
                                background: "#2f9bd6",
                                marginLeft: 6,
                              }}
                              onClick={async () => {
                                try {
                                  const { connected: status } = await fetchFixConnectionStatus(selectedPlatform);
                                  setConnected(status);
                                } catch {
                                  setConnected(null);
                                }
                              }}
                              disabled={actionLoading}
                            >
                              ⟳ Check Status
                            </Button>
                          </div>

                          {disconnectCooldown && (
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', padding: '4px 10px 0' }}>
                              Logout process in progress. Please try again after a 15 seconds.
                            </div>
                          )}

                          {actionError && (
                            <Alert
                              variant="danger"
                              className="py-2 px-3 mt-2"
                              style={{ fontSize: 12 }}
                            >
                              {actionError}
                            </Alert>
                          )}

                          {actionResult && (
                            <div
                              className="ecm-result-card"
                              style={
                                actionResult.connected
                                  ? styles.resultCard
                                  : styles.resultCardFail
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "10px",
                                }}
                              >
                                <span style={styles.resultLine}>
                                  <strong>Message:</strong> {actionResult.message}
                                </span>

                                {actionResult.logout_reason && (
                                  <span style={styles.resultLine}>
                                    <strong>Logout:</strong>{" "}
                                    {actionResult.logout_reason}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={styles.activityCard} >
                        <div style={styles.activityHeaderRow}>
                          <div style={styles.activityTitleRow}>
                            <span style={styles.activityAccentBar} />
                            <div>
                              <div style={styles.activityTitle}>Session Status</div>
                              <div style={styles.activitySubtitle}>
                                {selectedPlatform} · {lastFetched ? `Updated ${lastFetched}` : 'Loading…'}
                              </div>
                            </div>
                          </div>

                          <div style={styles.headerRight}>
                                {sessionData && (
                              <button
                                type="button"
                                className="ecm-icon-btn"
                                style={{ ...styles.iconBtn, ...(maxView ? styles.iconBtnActive : {}) }}
                                title={maxView ? 'Exit full view' : 'Full view — show all fields'}
                                onClick={() => setMaxView((v) => !v)}
                              >
                                {maxView ? '⤡' : '⤢'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="ecm-icon-btn"
                              style={styles.iconBtn}
                              title="Refresh"
                              onClick={fetchSessionStatus}
                              disabled={sessionLoading}
                            >
                              {sessionLoading ? '…' : '⟳'}
                            </button>

                          </div>
                        </div>

                        {sessionError && (
                          <Alert variant="danger" className="py-2 px-3" style={{ fontSize: 12 }}>
                            {sessionError}
                          </Alert>
                        )}

                        {sessionLoading && !sessionData && (
                          <div style={styles.centerRow}>
                            <Spinner animation="border" size="sm" />
                          </div>
                        )}

                        {sessionData && !maxView && (
                          <div style={styles.timeline}>
                            <div style={styles.timelineLine} />

                            {SESSION_ROW_META.map((meta) => {
                              const field = sessionData[meta.key];
                              const isOpen = expandedKeys.has(meta.key);
                              return (
                                <div key={meta.key} style={styles.tlRowOuter}>
                                  <div className="ecm-tl-row" style={styles.tlRow} onClick={() => toggleExpand(meta.key)}>
                                    <span style={styles.markerCol}>
                                      <span style={{ ...styles.tlDot, background: meta.dot }} />
                                    </span>
                                    <span style={styles.tlOuterCol}>
                                      <span style={styles.tlContent}>
                                        <span style={styles.tlLabel}>{meta.label}</span>
                                        <span style={styles.tlSep}>:</span>
                                        <span style={styles.tlTimePill}>{formatTime(field?.time)}</span>
                                      </span>
                                      <span style={{ ...styles.chevron, ...(isOpen ? styles.chevronOpen : {}) }}>▼</span>
                                    </span>
                                  </div>
                                  <div className={`ecm-expand-wrap${isOpen ? ' open' : ''}`}>
                                    <div className="ecm-expand-inner">
                                      <div style={styles.expandPanelOuter}>
                                        <div style={styles.expandPanel}>
                                          <span style={styles.expandPanelText}>
                                            <span style={styles.fieldPrefix}>Message:</span>
                                            {field?.message || 'No message available.'}
                                          </span>
                                          {field?.message && (
                                            <button
                                              type="button"
                                              className="ecm-copy"
                                              style={styles.copyBtn}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopy(meta.key, field.message);
                                              }}
                                            >
                                              {copiedKey === meta.key ? '✓ Copied' : 'Copy'}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            <div style={styles.tlRowOuter}>
                              <div
                                className="ecm-tl-row"
                                style={styles.tlRow}
                                onClick={() => toggleExpand(TRADE_CAPTURE_KEY)}
                              >
                                <span style={styles.markerCol}>
                                  <span
                                    style={{
                                      ...styles.tlDot,
                                      background: sessionData.trade_capture_status.status === 'SUCCESS' ? '#22c55e' : '#ef4444',
                                    }}
                                  />
                                </span>
                                <span style={styles.tlOuterCol}>
                                  <span style={styles.tlContent}>
                                    <span style={styles.tlLabel}>Trade Capture Status</span>
                                    <Badge
                                      bg={sessionData.trade_capture_status.status === 'SUCCESS' ? 'success' : 'danger'}
                                      style={{ fontSize: 10, padding: '2px 7px', width: 'fit-content' }}
                                    >
                                      {sessionData.trade_capture_status.status === 'SUCCESS' ? '🟢 Success' : '🔴 Failed'}
                                    </Badge>
                                  </span>
                                  <span
                                    style={{
                                      ...styles.chevron,
                                      ...(expandedKeys.has(TRADE_CAPTURE_KEY) ? styles.chevronOpen : {}),
                                    }}
                                  >
                                    ▼
                                  </span>
                                </span>
                              </div>
                              <div className={`ecm-expand-wrap${expandedKeys.has(TRADE_CAPTURE_KEY) ? ' open' : ''}`}>
                                <div className="ecm-expand-inner">
                                  <div style={styles.expandPanelOuter}>
                                    <div style={styles.expandPanel}>
                                      <span style={styles.expandPanelText}>
                                        <span style={styles.fieldPrefix}>Message:</span>
                                        {sessionData.trade_capture_status.message}
                                      </span>
                                      <button
                                        type="button"
                                        className="ecm-copy"
                                        style={styles.copyBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopy(TRADE_CAPTURE_KEY, sessionData.trade_capture_status.message);
                                        }}
                                      >
                                        {copiedKey === TRADE_CAPTURE_KEY ? '✓ Copied' : 'Copy'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {sessionData && maxView && (
                          <div style={styles.maxViewList}>
                            {SESSION_ROW_META.map((meta, i) => {
                              const field = sessionData[meta.key];
                              const isLast = i === SESSION_ROW_META.length - 1;
                              return (
                                <div key={meta.key} style={isLast ? styles.maxViewItemLast : styles.maxViewItem}>
                                  <div style={styles.maxViewHeadRow}>
                                    <span style={{ ...styles.tlDot, background: meta.dot }} />
                                    <span style={styles.maxViewLabel}>{meta.label}</span>
                                    <span style={styles.tlSep}>·</span>
                                    <span style={styles.fieldPrefix}>Time:</span>
                                    <span style={styles.maxViewTime}>{formatTime(field?.time)}</span>
                                    {field?.message && (
                                      <button
                                        type="button"
                                        className="ecm-copy"
                                        style={{ ...styles.copyBtn, marginLeft: 'auto' }}
                                        onClick={() => handleCopy(meta.key, field.message)}
                                      >
                                        {copiedKey === meta.key ? '✓ Copied' : 'Copy'}
                                      </button>
                                    )}
                                  </div>
                                  <div style={styles.fixTagGrid}>
                                    <span style={styles.fieldPrefix}>Message:</span>
                                    {field?.message || 'No message available.'}
                                  </div>
                                </div>
                              );
                            })}

                            <div style={styles.maxViewItemLast}>
                              <div style={styles.maxViewHeadRow}>
                                <span
                                  style={{
                                    ...styles.tlDot,
                                    background: sessionData.trade_capture_status.status === 'SUCCESS' ? '#22c55e' : '#ef4444',
                                  }}
                                />
                                <span style={styles.maxViewLabel}>Trade Capture Status</span>
                                <Badge
                                  bg={sessionData.trade_capture_status.status === 'SUCCESS' ? 'success' : 'danger'}
                                  style={{ fontSize: 10, padding: '2px 7px' }}
                                >
                                  {sessionData.trade_capture_status.status === 'SUCCESS' ? '🟢 Success' : '🔴 Failed'}
                                </Badge>
                                <button
                                  type="button"
                                  className="ecm-copy"
                                  style={{ ...styles.copyBtn, marginLeft: 'auto' }}
                                  onClick={() => handleCopy(TRADE_CAPTURE_KEY, sessionData.trade_capture_status.message)}
                                >
                                  {copiedKey === TRADE_CAPTURE_KEY ? '✓ Copied' : 'Copy'}
                                </button>
                              </div>
                              <div style={styles.fixTagGrid}>
                                <span style={styles.fieldPrefix}>Message:</span>
                                <span style={styles.fixTagValue}>{sessionData.trade_capture_status.message}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default ExchangeConnectivityManagement;